import React from 'react';

import Logo from './Logo.jsx';
import ShareButtons from './ShareButtons.jsx';
import Title from './Title.jsx';
import LongForm from './LongForm.jsx';
import Image from './Image.jsx';
import ImageGrid from './ImageGrid.jsx';
import AutoplayingVideo from './AutoplayingVideo.jsx';
import Bio from './Bio.jsx';
import Video from './Video.jsx';
import Advertisement from './Advertisement.jsx';
import RelatedContent from './RelatedContent.jsx';
import FreeForm from './FreeForm.jsx';
import SmallNav from './SmallNav.jsx';
import MediaLabStoryStore from './../stores/MediaLabStoryStore';
import GridGuides from './GridGuides.jsx';


class MediaLabStory extends React.Component {
  constructor(props) {
    super(props);


    // used by React as the component name
    this.displayName = 'MediaLabStory';


    // if there is an active animation frame
    this.ticking = false;


    // the previous scrollTop value
    this.previousScrollTop = 0;


    // if the headline and
    this.originalUiColor = 'dark';

    const data = this.props.data;
    if (data[0].type === 'title' &&
      data[0].title.headline_color !== '#000') {
      this.originalUiColor = 'light';
    }


    // how many pixels the user must scroll up before the intent was actually
    // considered to be scrolling up
    this.scrollUpThreshold = 20;


    // how many pixels the user has scrolled up since scrolling down
    this.scrollUpAmount = 0;


    // timeout to reset the scrolling up value
    this.scrollUpResetTimeout = 0;


    // Title data
    this.titleData = {};


    // initial state object
    this.state = {
      windowSize: {
        width: 0,
        height: 0,
      },
      scrollTop: 0,
      touch: false,
      scrollDirection: 'up',
      UIColor: this.originalUiColor,
    };


    // bind functions to component
    this.renderSections = this.renderSections.bind(this);
    this.onResize = this.onResize.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.scrollHandler = this.scrollHandler.bind(this);
    this.onStoreChange = this.onStoreChange.bind(this);
    this.resetScrollUpAmount = this.resetScrollUpAmount.bind(this);
  }


  /**
   * Called after component mounts to DOM
   * @returns {undefined} undefined
   */
  componentDidMount() {
    MediaLabStoryStore.addChangeListener(this.onStoreChange);
    window.addEventListener('resize', this.onResize);
    window.addEventListener('scroll', this.onScroll);

    this.onResize();
    this.onScroll();

    if (window.Modernizr) {
      this.setState({ touch: window.Modernizr.touchevents });
    }
  }


  /**
   * Called before component is removed from the DOM
   * @returns {undefined} undefined
   */
  componentWillUnmount() {
    MediaLabStoryStore.removeChangeListener(this.onStoreChange);
    window.removeEventListener('resize', this.onResize);
  }


  /**
   * Renders sections
   * @returns {Array<ReactElement>} An array of React elements
   */
  renderSections() {
    const data = this.props.data;
    const sections = [];

    for (let i = 0; i < data.length; i++) {
      const key = 'section-' + i + '-' + data[i].id;
      const prevSectionType = i > 0 ? data[i - 1].type : null;
      const imageData = {
        landscape: data[i].landscape_images,
        portrait: data[i].portrait_images,
        credit: data[i].credit,
        alt: data[i].alt,
        id: data[i].id,
      };
      let posterImage;

      switch (data[i].type) {
        case 'title':
          this.titleData = data[i];

          sections.push(
            <Title
              data={data[i]}
              iteration={i}
              key={key}
              originalUiColor={this.originalUiColor}
              prevSectionType={prevSectionType}
              scrollTop={this.state.scrollTop}
              touch={this.state.touch}
              windowSize={this.state.windowSize}
            />
          );

          break;

        case 'long-form':
          sections.push(
            <LongForm
              data={data[i]}
              iteration={i}
              key={key}
              prevSectionType={prevSectionType}
            />
          );

          break;

        case 'image-grid':
          sections.push(
            <ImageGrid
              data={data[i]}
              iteration={i}
              key={key}
              prevSectionType={prevSectionType}
              scrollTop={this.state.scrollTop}
              windowSize={this.state.windowSize}
            />
          );

          break;

        case 'image':
          sections.push(
            <Image
              data={imageData}
              iteration={i}
              key={key}
              lazy
              prevSectionType={prevSectionType}
              scrollTop={this.state.scrollTop}
              windowSize={this.state.windowSize}
            />
          );

          break;

        case 'video':
          sections.push(
            <Video
              data={data[i]}
              iteration={i}
              key={key}
              prevSectionType={prevSectionType}
              scrollTop={this.state.scrollTop}
              windowSize={this.state.windowSize}
            />
          );

          break;

        case 'autoplaying-video':
          if (data[i].landscape_images) {
            posterImage = data[i].landscape_images.large.url;
          } else {
            if (data[i].large_assets) {
              posterImage = data[i].large_assets.large;
            }
          }

          sections.push(
            <AutoplayingVideo
              elementId={'autoplaying-video-' + data[i].id}
              fullBleed
              id={data[i].id}
              iteration={i}
              key={key}
              loop
              posterImageUrl={posterImage}
              prevSectionType={prevSectionType}
              scrollPadding={200}
              scrollTop={this.state.scrollTop}
              touch={this.state.touch}
              videoUrl={data[i].video_url}
              videoWidth={1600}
              videoHeight={900}
              windowSize={this.state.windowSize}
            />
          );

          break;

        case 'ad':
          sections.push(
            <Advertisement
              data={data[i]}
              iteration={i}
              key={key}
              prevSectionType={prevSectionType}
              scrollTop={this.state.scrollTop}
              windowSize={this.state.windowSize}
            />
          );

          break;

        case 'clean-slate':
          sections.push(
            <FreeForm
              data={data[i]}
              iteration={i}
              key={key}
              scrollTop={this.state.scrollTop}
              windowSize={this.state.windowSize}
            />
          );

          break;

        case 'free-form':
          sections.push(
            <FreeForm
              data={data[i]}
              iteration={i}
              key={key}
              scrollTop={this.state.scrollTop}
              windowSize={this.state.windowSize}
            />
          );

          break;

        case 'related-content':
          sections.push(
            <RelatedContent
              data={data[i]}
              iteration={i}
              key={key}
              prevSectionType={prevSectionType}
            />
          );

          break;

        case 'bio':
          sections.push(
            <Bio
              data={data[i]}
              iteration={i}
              key={key}
              prevSectionType={prevSectionType}
            />
          );

          break;

        default:
          break;
      }
    }

    return sections;
  }


  /**
   * Scroll handler
   * @returns {undefined} undefined
   */
  onScroll() {
    if (typeof document !== 'undefined') {
      const doc = document.documentElement;
      const scrollTop = (window.pageYOffset || doc.scrollTop) -
        (doc.clientTop || 0);

      this.scrollHandler(scrollTop);
    }
  }


  /**
   * Resets the amount the user has scrolled up
   * @returns {undefined} undefined
   */
  resetScrollUpAmount() {
    this.scrollUpAmount = 0;
  }


  /**
   * Called on scroll, during the browser's animation frame cycle
   * @param {Number} scrollTop - The amount the user has scrolled
   * @returns {undefined} undefined
   */
  scrollHandler(scrollTop) {
    if (!this.ticking) {
      this.ticking = true;

      window.requestAnimationFrame(() => {
        clearTimeout(this.scrollUpResetTimeout);

        this.setState({ scrollTop }, () => {
          this.ticking = false;

          if (this.state.scrollTop >= this.previousScrollTop) {
            this.resetScrollUpAmount();

            if (this.state.scrollDirection !== 'down') {
              this.setState({ scrollDirection: 'down' });
            }
          } else {
            this.scrollUpAmount += 1;

            if (this.scrollUpAmount >= this.scrollUpThreshold) {
              this.resetScrollUpAmount();

              if (this.state.scrollDirection !== 'up') {
                this.setState({ scrollDirection: 'up' });
              }
            }
          }

          this.scrollUpResetTimeout = setTimeout(this.resetScrollUpAmount, 50);

          this.previousScrollTop = this.state.scrollTop;
        });
      });
    }
  }


  /**
   * Resize handler
   * @returns {undefined} undefined
   */
  onResize() {
    if (typeof window !== 'undefined') {
      const windowSize = {
        width: window.innerWidth,
        height: window.innerHeight,
      };

      this.setState({
        windowSize,
        scrollDirection: 'up',
      });
    }
  }


  /**
   * Called when a 'CHANGE' event is emitted from the MediaLabStoryStore
   * @returns {ReactElement} ReactElement
   */
  onStoreChange() {
    this.setState(MediaLabStoryStore.getState());
  }


  /**
   * Renders component
   * @returns {ReactElement} ReactElement
   */
  render() {
    const sections = this.renderSections();
    const uiColorClass = ' ' + this.state.UIColor + '-ui';
    const additionalClass = this.props.additionalClassName ? ' ' +
      this.props.additionalClassName : '';
    const scrollDirectionClass = ' scrolling-' + this.state.scrollDirection;
    const scrolledPastTitleClass = this.state.scrolledPastTitle ?
      ' scrolled-past-title' : '';
    const className = 'component-media-lab-story' +
      uiColorClass +
      additionalClass +
      scrollDirectionClass +
      scrolledPastTitleClass;

    return (
      <div className={className}>
        <Logo
          url={this.props.logoUrl}
        />
        <ShareButtons
          shareText={this.titleData.share_text}
          storyTitle={this.titleData.title.headline}
          storyUrl={this.props.storyUrl}
          twitterHandle={this.titleData.twitter_handle}
        />
        <div className='section-wrapper'>
          <div className='sections grid'>
            {sections}
          </div>
        </div>
        <SmallNav
          logoUrl={this.props.logoUrl}
          shareText={this.titleData.share_text}
          storyTitle={this.titleData.title.headline}
          storyUrl={this.props.storyUrl}
          twitterHandle={this.titleData.twitter_handle}
        />
      </div>
    );
  }
}


/**
 * Expected propTypes
 * @prop {String} additionalClassName - An additional className to add to the
 * component
 * @prop {Array<Object>} data - The story data
 * @prop {String} logoUrl - The url for the logo
 * @prop {String} storyUrl - The url of the story
 */
MediaLabStory.propTypes = {
  additionalClassName: React.PropTypes.string,
  data: React.PropTypes.array.isRequired,
  logoUrl: React.PropTypes.string,
  storyUrl: React.PropTypes.string.isRequired,
};


export default MediaLabStory;



/** WEBPACK FOOTER **
 ** ./assets/js/src/components/MediaLabStory.jsx
 **/